import {reportError} from '@github-ui/failbot'
import {useCallback} from 'react'

import {SystemColumnId} from '../../api/columns/contracts/memex-column'
import type {LabelSuggestionAttribution} from '../../api/memex-items/api-get-label-suggestions'
import {apiGetSuggestedLabels} from '../../api/memex-items/api-get-suggested-labels'
import type {SuggestedLabel} from '../../api/memex-items/contracts'
import {ItemType} from '../../api/memex-items/item-type'
import {apiSidePanelGetSuggestedLabelsForItem} from '../../api/side-panel/api-side-panel-get-suggestions'
import {ItemKeyType} from '../../api/side-panel/contracts'
import {getEnabledFeatures} from '../../helpers/feature-flags'
import type {MemexItemModel} from '../../models/memex-item-model'
import {fetchLabelSuggestions} from '../../queries/label-suggestions'
import {useSetColumnValue} from '../column-values/use-set-column-value'
import {useFindMemexItem} from '../memex-items/use-find-memex-item'
import {makeApiCall} from './suggestions-state-provider'
import {useSuggestionsContext} from './use-suggestions-context'

type FetchSuggestedLabelsHookType = {
  /**
   * Fetches the suggested labels from the server for a given item.
   * Updates the item's labels column with the suggested labels that are marked
   * as selected.
   * @returns A promise that resolves to an array of suggested labels.
   * @param memexItemId The id of the item to fetch the suggested labels for.
   */
  fetchSuggestedLabels: (model: MemexItemModel) => Promise<Array<SuggestedLabel> | Error | undefined>
}

/**
 * Suggestions are computed per repository, so they only exist for items backed
 * by one. Draft issues have no repository and are skipped entirely rather than
 * issuing a request that cannot succeed.
 */
async function fetchSuggestionAttributions(model: MemexItemModel): Promise<Array<LabelSuggestionAttribution>> {
  // Projects-side kill switch. Gating the request itself (rather than only the
  // rendering) means a disabled flag costs no extra traffic at all.
  if (!getEnabledFeatures().memex_label_suggestions) return []

  if (model.contentType !== ItemType.Issue) return []

  const nameWithOwner = model.getNameWithOwner?.()
  const [owner, repo] = nameWithOwner ?? []
  if (!owner || !repo) return []

  try {
    return await fetchLabelSuggestions({owner, repo})
  } catch (error) {
    // The picker renders exactly as it does today without a suggestions group,
    // and this request is awaited alongside the primary label fetch, so a
    // failure here must never fail that fetch or surface to the user.
    //
    // It must still reach engineers, though: a bare `catch` would leave a
    // suggestions outage completely invisible. Letting the error bubble — the
    // usual way errors reach failbot — is not an option, because `Promise.all`
    // is what catches it and doing so would take the label list down with it.
    // Reporting keeps failbot's filtering of expected network errors, so a
    // viewer's flaky connection does not page anyone.
    //
    // Note this is a genuine failure. It is distinct from the endpoint
    // answering with `errors` (see `apiGetLabelSuggestions`), which is the
    // documented response for an unreadable repository and is not reported.
    reportError(error, {message: 'Failed to fetch label suggestions', reactAppName: 'memex'})

    return []
  }
}

/**
 * Attaches server attribution to the labels that were suggested, leaving every
 * other label untouched. Returns new objects so the undecorated list handed to
 * `setColumnValue` keeps its existing shape.
 */
function decorateWithSuggestions(
  labels: Array<SuggestedLabel>,
  attributions: Array<LabelSuggestionAttribution>,
): Array<SuggestedLabel> {
  if (attributions.length === 0) return labels

  const attributionsById = new Map(attributions.map(attribution => [attribution.labelId, attribution]))

  return labels.map(label => {
    const attribution = attributionsById.get(label.id)
    if (!attribution) return label

    return {
      ...label,
      suggestionReason: attribution.reason,
      suggestionRank: attribution.rank,
      suggestionOrder: attribution.order,
    }
  })
}

export const useFetchSuggestedLabels = (): FetchSuggestedLabelsHookType => {
  const {findMemexItem} = useFindMemexItem()
  const {setColumnValue} = useSetColumnValue()
  const {getSuggestedLabelsForItem, setSuggestedLabelsForItem} = useSuggestionsContext()

  const fetchSuggestedLabels = useCallback(
    async (model: MemexItemModel) => {
      const suggestionId = model.getSuggestionsCacheKey()
      const labels = getSuggestedLabelsForItem(suggestionId)

      if (!(labels instanceof Error) && labels?.length) {
        return labels
      }

      const [{response, error}, attributions] = await Promise.all([
        makeApiCall(() => {
          if (model.memexItemId?.()) {
            return apiGetSuggestedLabels({memexProjectItemId: model.memexItemId()})
          } else {
            switch (model.contentType) {
              case ItemType.DraftIssue:
                return apiSidePanelGetSuggestedLabelsForItem({
                  kind: ItemKeyType.PROJECT_DRAFT_ISSUE,
                  projectItemId: model.id,
                })
              case ItemType.Issue:
                return apiSidePanelGetSuggestedLabelsForItem({
                  kind: ItemKeyType.ISSUE,
                  itemId: model.itemId(),
                  repositoryId: model.ownerId(),
                })
              default:
                throw new Error(`Unsupported item type: ${model.contentType}`)
            }
          }
        }),
        fetchSuggestionAttributions(model),
      ])

      const memexItem = findMemexItem(model.id)

      if (memexItem && !error) {
        setColumnValue(memexItem, {
          memexProjectColumnId: SystemColumnId.Labels,
          value:
            response?.suggestions
              .filter(suggestedLabel => suggestedLabel.selected)
              .sort((a, b) => a.name.localeCompare(b.name)) || null,
        })
      }

      const result = response?.suggestions ? decorateWithSuggestions(response.suggestions, attributions) : error

      setSuggestedLabelsForItem(suggestionId, result)

      return result
    },
    [getSuggestedLabelsForItem, setSuggestedLabelsForItem, findMemexItem, setColumnValue],
  )

  return {fetchSuggestedLabels}
}
